import { useState } from "react";
import {
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Grid,
  InputAdornment,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { CouponTemplateFormDto, ValueType } from "../../../store/models/coupon/CouponManagementDto";

interface CouponRewardValueStepProps {
  formData: CouponTemplateFormDto;
  updateFormData: (updates: Partial<CouponTemplateFormDto>) => void;
  readOnly?: boolean;
}

const CouponRewardValueStep = ({ formData, updateFormData, readOnly = false }: CouponRewardValueStepProps) => {
  const { t } = useTranslation();
  const [touchedFields, setTouchedFields] = useState<{ [key: string]: boolean }>({});

  const handleFieldTouch = (fieldName: string) => {
    setTouchedFields((prev) => ({ ...prev, [fieldName]: true }));
  };

  const handleValueTypeChange = (newValueType: ValueType) => {
    const shouldClearValue = newValueType === ValueType.GIFT || newValueType === ValueType.FREE_SHIPPING;
    updateFormData({
      valueType: newValueType,
      value: shouldClearValue ? "" : formData.value,
    });
  };

  const shouldShowValueField = () => {
    return formData.valueType === ValueType.PERCENTAGE || formData.valueType === ValueType.FIXED_AMOUNT;
  };

  const getValueLabel = () => {
    if (formData.valueType === ValueType.PERCENTAGE) {
      return t("coupon.form.discountPercentage");
    }
    if (formData.valueType === ValueType.FIXED_AMOUNT) {
      return t("coupon.form.discountAmount");
    }
    return t("coupon.form.value");
  };

  const getValueAdornment = () => {
    switch (formData.valueType) {
      case ValueType.PERCENTAGE:
        return <InputAdornment position="end">%</InputAdornment>;
      case ValueType.FIXED_AMOUNT:
        return <InputAdornment position="end">€</InputAdornment>;
      default:
        return undefined;
    }
  };

  const getValuePlaceholder = () => {
    switch (formData.valueType) {
      case ValueType.PERCENTAGE:
        return "e.g., 20";
      case ValueType.FIXED_AMOUNT:
        return "e.g., 15.00";
      default:
        return t("coupon.form.errors.enterValue");
    }
  };

  const getValueError = () => {
    const value = Number.parseFloat(formData.value);
    if (!formData.value || formData.value === "") return "";

    if (formData.valueType === ValueType.PERCENTAGE) {
      if (value < 0) return t("coupon.form.errors.percentageNegative");
      if (value > 100) return t("coupon.form.errors.percentageExceed");
    } else if (formData.valueType === ValueType.FIXED_AMOUNT) {
      if (value < 0) return t("coupon.form.errors.amountNegative");
    }
    return "";
  };

  const hasValueError = () => {
    return touchedFields.value && (getValueError() !== "" || !formData.value.trim());
  };

  const getValueHelperText = () => {
    if (!touchedFields.value) return "";

    const valueError = getValueError();
    if (valueError) return valueError;

    if (!formData.value.trim()) {
      return t("coupon.form.errors.valueRequired");
    }

    return "";
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold", color: "#3859ff" }}>
        {t("coupon.form.rewardAndValue")}
      </Typography>

      <Grid container spacing={3} sx={{ mt: 1 }}>
        {/* Reward Type Dropdown */}
        <Grid item xs={12} md={4}>
          <FormControl fullWidth>
            <InputLabel>{t("coupon.form.rewardType")}</InputLabel>
            <Select
              value={formData.valueType}
              onChange={(e) => handleValueTypeChange(e.target.value as ValueType)}
              label={t("coupon.form.rewardType")}
              sx={{ backgroundColor: "white" }}
              disabled={readOnly}
            >
              <MenuItem value={ValueType.PERCENTAGE}>{t("coupon.valueType.percentage")}</MenuItem>
              <MenuItem value={ValueType.FIXED_AMOUNT}>{t("coupon.valueType.fixed_amount")}</MenuItem>
              <MenuItem value={ValueType.GIFT}>{t("coupon.valueType.gift")}</MenuItem>
              <MenuItem value={ValueType.FREE_SHIPPING}>{t("coupon.valueType.free_shipping")}</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {/* Dynamic Value Input */}
        <Grid item xs={12} md={4}>
          {shouldShowValueField() ? (
            <TextField
              fullWidth
              type="number"
              label={getValueLabel()}
              value={formData.value}
              onChange={(e) => updateFormData({ value: e.target.value })}
              onBlur={() => handleFieldTouch("value")}
              placeholder={getValuePlaceholder()}
              error={hasValueError()}
              helperText={getValueHelperText()}
              InputProps={{
                endAdornment: getValueAdornment(),
              }}
              inputProps={{
                min: 0,
                max: formData.valueType === ValueType.PERCENTAGE ? 100 : undefined,
                step: formData.valueType === ValueType.FIXED_AMOUNT ? "0.01" : "1",
              }}
              sx={{ backgroundColor: "white" }}
              required
              disabled={readOnly}
            />
          ) : (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                height: "56px",
                px: 2,
                backgroundColor: "#f5f5f5",
                borderRadius: 1,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                {formData.valueType === ValueType.FREE_SHIPPING
                  ? t("coupon.form.freeShippingNoValue")
                  : t("coupon.form.giftNoMonetaryValue")}
              </Typography>
            </Box>
          )}
        </Grid>

        {/* Minimum Order Value */}
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            type="number"
            label={t("coupon.form.minimumOrderValue")}
            value={formData.minimumPurchaseAmount || ""}
            onChange={(e) => updateFormData({ minimumPurchaseAmount: Number.parseFloat(e.target.value) || undefined })}
            onBlur={() => handleFieldTouch("minimumPurchaseAmount")}
            placeholder="e.g., 50.00"
            InputProps={{
              endAdornment: <InputAdornment position="end">€</InputAdornment>,
            }}
            inputProps={{
              min: 0,
              step: "0.01",
            }}
            sx={{ backgroundColor: "white" }}
            disabled={readOnly}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default CouponRewardValueStep;
